import { useQueryClient } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useCallback, useState, useSyncExternalStore } from "react";
import type { z } from "zod";
import {
  type CustomerContextItemSchema,
  CustomerNoteResponseSchema,
  CustomerNoteUpdateSchema,
} from "../../shared/customer-record-schemas";
import { documentMarkdown } from "../knowledge/document-markdown";
import { ApiClientError, parseResponse } from "../api/client-core";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { DocumentBody } from "../knowledge/DocumentBody";
import { DocumentEditor } from "../knowledge/DocumentEditor";
import { DocumentSession } from "../knowledge/document-session";
import { SaveStatus, RecoveryNotice } from "./DocsDocumentCanvas";

type Note = z.infer<typeof CustomerContextItemSchema>;
function noteMarkdown(note: Note) {
  return note.metadata["bodyFormat"] === "markdown"
    ? (note.bodyText ?? "")
    : documentMarkdown([{ id: "legacy_note", type: "paragraph", text: note.bodyText ?? "" }]);
}

async function updateNote(
  organizationId: string,
  note: Note,
  draft: { title: string; markdown: string },
) {
  return parseResponse(
    await fetch(
      `/api/ops/customers/${encodeURIComponent(organizationId)}/context/notes/${encodeURIComponent(note.id)}`,
      {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          CustomerNoteUpdateSchema.parse({
            toolId: note.toolId ?? null,
            expected: { title: note.title, bodyText: note.bodyText, updatedAt: note.updatedAt },
            title: draft.title,
            bodyText: draft.markdown || null,
          }),
        ),
      },
    ),
    CustomerNoteResponseSchema,
  );
}

function useNoteSession(note: Note, organizationId: string) {
  const client = useQueryClient();
  const [session] = useState(() => {
    let saved = note;
    return new DocumentSession({
      draft: { title: note.title, markdown: noteMarkdown(note), links: [] },
      revision: 1,
      isConflict: (error) => error instanceof ApiClientError && error.status === 409,
      save: async (draft, revision) => {
        const response = await updateNote(organizationId, saved, draft);
        saved = response.note;
        void client.invalidateQueries({ queryKey: ["ops", "context", organizationId] });
        return revision + 1;
      },
    });
  });
  return session;
}

function NoteEditor({
  note,
  organizationId,
  onClose,
}: {
  note: Note;
  organizationId: string;
  onClose: () => void;
}) {
  const session = useNoteSession(note, organizationId);
  const mount = useCallback((node: HTMLDivElement | null) => session.attach(node), [session]);
  const { draft, state } = useSyncExternalStore(session.subscribe, session.snapshot);
  const close = () => {
    if (!session.dirty() || window.confirm("This note has unsaved changes. Close it?")) onClose();
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Edit note</DialogTitle>
          <DialogDescription>
            {note.toolId ? "Tool-specific" : "Company-wide"} · Internal knowledge
          </DialogDescription>
        </DialogHeader>
        <DialogBody ref={mount} className="sm:px-8">
          <textarea
            aria-label="Note title"
            rows={1}
            maxLength={240}
            className="mb-5 w-full resize-none bg-transparent text-3xl font-semibold outline-none [field-sizing:content] focus-visible:shadow-[var(--focus-ring)]"
            value={draft.title}
            onChange={(event) => {
              session.change({ title: event.target.value.replace(/\n/g, " ") });
            }}
          />
          <RecoveryNotice session={session} />
          <DocumentEditor
            markdown={draft.markdown}
            onChange={(markdown) => {
              session.change({ markdown });
            }}
            onSave={() => {
              void session.flush();
            }}
          />
        </DialogBody>
        <DialogFooter
          start={
            <>
              <SaveStatus session={session} />
              <p className="m-0 text-sm text-muted-foreground">
                {draft.markdown.length.toLocaleString()} / 20,000 characters
              </p>
            </>
          }
        >
          <Button
            disabled={state === "saving" || state === "conflict"}
            onClick={() => {
              void session.flush().then(() => {
                if (!session.dirty()) onClose();
              });
            }}
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CustomerNoteCard({
  note,
  organizationId,
  canWrite,
}: {
  note: Note;
  organizationId: string;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <article className="rounded-md border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          {note.title}
        </h4>
        {canWrite ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditing(true);
            }}
          >
            Edit note
          </Button>
        ) : null}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Internal · Updated {new Date(note.updatedAt * 1000).toLocaleDateString()}
      </p>
      <div className="mt-3 max-h-48 overflow-hidden [&_.document-prose]:min-h-0 [&_.document-prose]:text-sm">
        <DocumentBody markdown={noteMarkdown(note)} />
      </div>
      <Dialog>
        <DialogTrigger render={<Button size="sm" variant="ghost" className="mt-3" />}>
          Read note
        </DialogTrigger>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{note.title}</DialogTitle>
            <DialogDescription>
              Internal knowledge · {note.toolId ? "This tool" : "This customer"}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <DocumentBody markdown={noteMarkdown(note)} />
          </DialogBody>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" />}>Done</DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {editing ? (
        <NoteEditor
          note={note}
          organizationId={organizationId}
          onClose={() => {
            setEditing(false);
          }}
        />
      ) : null}
    </article>
  );
}
